import React from "react";
import FitGauge from "./FitGauge";
import RichText from "./RichText";
import LegalStrip from "./LegalStrip";

const DISTRIBUTION_LAYOUT = [
  { x: 20, w: 110, valX: 75 },
  { x: 148, w: 110, valX: 203 },
  { x: 276, w: 110, valX: 331 },
  { x: 404, w: 110, valX: 459 }
];

const PropertyAlignment = ({ alignment, agent }) => {
  if (!alignment) return null;

  const distribution = alignment.distribution || [];
  const maxCount = Math.max(1, ...distribution.map((d) => d.count));

  return (
    <section id="alignment" className="abr-section" aria-labelledby="abr-alignment-title">
      <div className="abr-eyebrow">Property Alignment</div>
      <h2 className="abr-h1" id="abr-alignment-title">
        {alignment.title}
      </h2>
      <p className="abr-lede">{alignment.lede}</p>

      <FitGauge fit={alignment.fit} />

      <div className="abr-align-grid">
        <div>
          <div className="abr-align-col-head is-meets">What you align on</div>
          <div className="abr-align-list">
            {(alignment.meets || []).map((item, idx) => (
              <div className="abr-align-row is-meets" key={`meets-${idx}`}>
                <div className="abr-align-glyph">&#10003;</div>
                <div className="abr-align-label">{item.label}</div>
                <div className="abr-align-pct">{item.pct}% of matches</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="abr-align-col-head">Where the gap is</div>
          <div className="abr-align-list">
            {(alignment.gaps || []).map((item, idx) => (
              <div className="abr-align-row is-gap" key={`gap-${idx}`}>
                <div className="abr-align-glyph">&minus;</div>
                <div className="abr-align-label">{item.label}</div>
                <div className="abr-align-pct">{item.pct}% of matches</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {distribution.length ? (
        <div style={{ marginTop: 36 }}>
          <div className="abr-card-title">How well your matches fit</div>
          <p className="abr-card-sub">
            Each matched buyer gets a 0-100 fit score against your home. Here's how they break down.
          </p>
          <svg
            viewBox="0 0 600 110"
            width="100%"
            height="140"
            role="img"
            aria-label={`Match score distribution: ${distribution.map((d) => `${d.count} in ${d.label}`).join(", ")}`}
          >
            <g transform="translate(45, 8)">
              <line x1="0" y1="74" x2="510" y2="74" stroke="#D1D5DB" strokeWidth="0.5" />
              {distribution.map((d, idx) => {
                const layout = DISTRIBUTION_LAYOUT[idx] || { x: 0, w: 110, valX: 0 };
                const ratio = d.count / maxCount;
                const height = Math.max(4, Math.round(60 * ratio));
                let fill = "rgba(42,47,59,1.0)";
                if (d.accent) fill = "#2D5F73";
                else if (d.tone === "mid") fill = "rgba(42,47,59,0.50)";
                else if (d.tone === "dim") fill = "rgba(42,47,59,0.18)";
                return (
                  <g key={idx}>
                    <rect x={layout.x} y={74 - height} width={layout.w} height={height} fill={fill} rx="2" />
                    <text
                      x={layout.valX}
                      y={74 - height - 4}
                      className="abr-bar-value"
                      textAnchor="middle"
                      fontSize="11"
                      fill={d.accent ? "#2D5F73" : d.count === 0 ? "#6B7280" : undefined}
                    >
                      {d.count}
                    </text>
                    <text
                      x={layout.valX}
                      y="92"
                      className="abr-axis-tick"
                      textAnchor="middle"
                      fontSize="9"
                      fill={d.accent ? "#2D5F73" : undefined}
                      fontWeight={d.accent ? 700 : undefined}
                    >
                      {d.label}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
          <p className="abr-insight-caption">{alignment.distributionCaption}</p>
        </div>
      ) : null}

      <div className="abr-bottom-line">
        <span className="abr-bottom-line-label">Bottom line</span>
        <RichText as="span" className="abr-bottom-line-text" text={alignment.bottomLine} />
      </div>

      <LegalStrip agent={agent} />
    </section>
  );
};

export default PropertyAlignment;
